import styled from "styled-components";

export const FormCard = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  width: 100%;
  max-width: 560px;
  margin: 1rem auto 0;
  padding: 1.5rem 1.35rem;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--info) 18%, transparent);
  background: var(--surface-glass);
`;

export const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const Label = styled.label`
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--text-heading);
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border-radius: 10px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-overlay);
  color: var(--text-primary);
  font-size: 0.98rem;

  &::placeholder {
    color: var(--input-placeholder);
  }

  &:focus {
    outline: none;
    border-color: var(--info);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--info) 18%, transparent);
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 0.7rem 0.9rem;
  border-radius: 10px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-overlay);
  color: var(--text-primary);
  font-size: 0.95rem;
  resize: vertical;
  font-family: inherit;

  &::placeholder {
    color: var(--input-placeholder);
  }

  &:focus {
    outline: none;
    border-color: var(--info);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--info) 18%, transparent);
  }
`;

export const Hint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

export const SubmitButton = styled.button`
  margin-top: 0.35rem;
  padding: 0.75rem 1.2rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  color: var(--text-on-brand);
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.72;
    cursor: wait;
  }
`;

export const SecondaryLink = styled.button`
  align-self: flex-start;
  margin-top: 0.25rem;
  padding: 0;
  border: none;
  background: none;
  color: var(--info);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.85;
  }
`;

export const Feedback = styled.div`
  margin-top: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1px solid
    ${(p) => {
      if (p.$variant === "error") {
        return "color-mix(in srgb, var(--danger) 35%, transparent)";
      }
      if (p.$variant === "warning") {
        return "color-mix(in srgb, var(--warning) 45%, transparent)";
      }
      return "color-mix(in srgb, var(--success) 35%, transparent)";
    }};
  background: ${(p) => {
    if (p.$variant === "error") {
      return "color-mix(in srgb, var(--danger) 12%, transparent)";
    }
    if (p.$variant === "warning") {
      return "color-mix(in srgb, var(--warning) 14%, transparent)";
    }
    return "color-mix(in srgb, var(--success) 12%, transparent)";
  }};
  color: var(--text-primary);
  font-size: 0.9rem;
  line-height: 1.45;
`;

export const EmailStatusBanner = styled.div`
  margin: 0.65rem 0 0.35rem;
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  border: 1px solid
    ${(p) => {
      if (p.$status === "sent") {
        return "color-mix(in srgb, var(--success) 45%, transparent)";
      }
      if (p.$status === "failed") {
        return "color-mix(in srgb, var(--danger) 45%, transparent)";
      }
      return "color-mix(in srgb, var(--warning) 45%, transparent)";
    }};
  background: ${(p) => {
    if (p.$status === "sent") {
      return "color-mix(in srgb, var(--success) 16%, transparent)";
    }
    if (p.$status === "failed") {
      return "color-mix(in srgb, var(--danger) 14%, transparent)";
    }
    return "color-mix(in srgb, var(--warning) 14%, transparent)";
  }};
  color: var(--text-heading);
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.4;

  span {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.82rem;
    font-weight: 500;
    color: var(--text-secondary);
  }
`;

export const OutcomeBadge = styled.span`
  display: inline-block;
  margin-right: 0.5rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--text-on-brand);
  background: ${(p) => {
    if (p.$outcome === "existing_user") return "var(--info)";
    if (p.$outcome === "created_user_request") return "var(--warning)";
    return "var(--success)";
  }};
`;

export const MetaList = styled.ul`
  margin: 0.65rem 0 0;
  padding-left: 1.1rem;
  color: var(--text-muted);
  font-size: 0.85rem;

  li {
    margin-bottom: 0.25rem;
  }
`;

export const CurrentLinksCard = styled.div`
  width: 100%;
  max-width: 560px;
  margin: 0 auto 0.5rem;
  padding: 1rem 1.15rem;
  border-radius: 14px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-overlay);
  font-size: 0.9rem;
  color: var(--text-primary);

  strong {
    color: var(--text-heading);
  }

  a {
    color: var(--info);
    word-break: break-all;
  }
`;

export const EmptyLinks = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 0.88rem;
`;

export const OutlineButton = styled.button`
  align-self: stretch;
  padding: 0.72rem 1.15rem;
  border-radius: 10px;
  border: 1.5px solid var(--border-default);
  background: var(--surface-overlay);
  color: var(--text-heading);
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: var(--info);
    transform: translateY(-1px);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: color-mix(in srgb, var(--brand-navy) 55%, transparent);
  backdrop-filter: blur(4px);
`;

export const ModalPanel = styled.div`
  position: relative;
  width: min(100%, 560px);
  max-height: min(92vh, 820px);
  overflow-y: auto;
  padding: 1.35rem 1.25rem 1.4rem;
  border-radius: 18px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-1);
  box-shadow: var(--shadow-lg);
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-heading);
`;

export const ModalSubtitle = styled.p`
  margin: 0.4rem 0 0;
  font-size: 0.88rem;
  line-height: 1.45;
  color: var(--text-muted);
`;

export const ModalCloseButton = styled.button`
  flex-shrink: 0;
  width: 2.1rem;
  height: 2.1rem;
  border: none;
  border-radius: 999px;
  background: var(--surface-3);
  color: var(--text-primary);
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: var(--surface-hover);
  }
`;

export const ModalFormCard = styled(FormCard)`
  margin: 0.75rem 0 0;
  max-width: none;
  padding: 0;
  border: none;
  background: transparent;
`;

export const ModalLinksCard = styled(CurrentLinksCard)`
  max-width: none;
  margin: 0;
`;

export const PageIntro = styled.p`
  margin: 0 auto;
  width: 100%;
  max-width: 920px;
  font-size: 0.92rem;
  font-weight: 400;
  font-style: italic;
  line-height: 1.5;
  letter-spacing: 0.01em;
  color: var(--text-muted);
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.86rem;
    max-width: 100%;
  }
`;
